import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Divider, Typography } from "@mui/material";
import { Email, ImageOutlined, UnfoldMore, UnfoldLess } from "@mui/icons-material";
import api from "../../services/api";

// Mensagem do canal de E-MAIL (mediaType "email") dentro da conversa.
// Cabeçalho (assunto, de, para, cc) + corpo HTML JÁ SANITIZADO no backend
// (helpers/emailHtml), mostrado num <iframe sandbox> SEM scripts e com CSP —
// segunda camada de proteção. Imagens remotas ficam desligadas (rastreio de
// abertura) até o atendente clicar em "Mostrar imagens". Os anexos aparecem
// como mensagens de arquivo logo abaixo, como nos outros canais.

const cache = new Map();
const ALTURA_MAX = 420;

const origemBackend = () => {
	try {
		return new URL(process.env.REACT_APP_BACKEND_URL || window.location.origin).origin;
	} catch (_) {
		return window.location.origin;
	}
};

const montaDocumento = (html, mostrarRemotas) => {
	const corpo = mostrarRemotas ? html.replace(/data-remote-src="/g, 'src="') : html;
	const img = `data: ${origemBackend()}${mostrarRemotas ? " https: http:" : ""}`;
	return `<!doctype html><html><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src ${img}; style-src 'unsafe-inline'; font-src data:">
<base target="_blank">
<style>html,body{margin:0;padding:0}body{padding:10px 12px;font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.45;color:#222;background:#fff;overflow-wrap:anywhere}img{max-width:100%;height:auto}table{max-width:100%}blockquote{margin:0 0 0 .8ex;border-left:2px solid #ccc;padding-left:1ex;color:#555}</style>
</head><body>${corpo}</body></html>`;
};

const EmailMessageView = ({ message }) => {
	const [meta, setMeta] = useState(cache.get(message.id) || null);
	const [erro, setErro] = useState(false);
	const [remotas, setRemotas] = useState(false);
	const [expandido, setExpandido] = useState(false);
	const [altura, setAltura] = useState(120);
	const frame = useRef(null);

	useEffect(() => {
		if (cache.has(message.id)) return;
		let vivo = true;
		const busca = async (tentativa = 0) => {
			try {
				const { data } = await api.get(`/email/messages/${encodeURIComponent(message.id)}`);
				cache.set(message.id, data);
				if (vivo) setMeta(data);
			} catch (e) {
				if (tentativa < 2) setTimeout(() => busca(tentativa + 1), 800);
				else if (vivo) setErro(true);
			}
		};
		busca();
		return () => {
			vivo = false;
		};
	}, [message.id]);

	const documento = useMemo(
		() => (meta?.html ? montaDocumento(meta.html, remotas) : null),
		[meta, remotas]
	);

	const mede = useCallback(() => {
		try {
			const doc = frame.current?.contentDocument;
			if (!doc?.body) return;
			setAltura(Math.max(60, doc.documentElement.scrollHeight));
		} catch (_) {
			/* sem acesso: mantém a altura */
		}
	}, []);

	const aoCarregar = () => {
		mede();
		try {
			const doc = frame.current?.contentDocument;
			doc?.querySelectorAll("img").forEach(i => i.addEventListener("load", mede));
		} catch (_) {
			/* idem */
		}
		setTimeout(mede, 400);
	};

	const alturaVisivel = expandido ? altura : Math.min(altura, ALTURA_MAX);

	return (
		<Box sx={{ width: "min(640px, 62vw)", maxWidth: "100%", pb: 2 }} data-email-view={message.id}>
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
				<Email sx={{ fontSize: 18, color: "#004f9f" }} />
				<Typography variant="subtitle2" sx={{ fontWeight: 700, wordBreak: "break-word" }} data-email-subject>
					{meta?.subject || (erro ? "E-mail" : "Carregando e-mail…")}
				</Typography>
			</Box>
			{meta && (
				<Box sx={{ fontSize: 12, color: "text.secondary", mb: 0.75, lineHeight: 1.5 }}>
					<div>
						<b>De:</b> {meta.fromName ? `${meta.fromName} <${meta.fromAddress}>` : meta.fromAddress}
					</div>
					{meta.to && (
						<div>
							<b>Para:</b> {meta.to}
						</div>
					)}
					{meta.cc && (
						<div>
							<b>Cc:</b> {meta.cc}
						</div>
					)}
				</Box>
			)}
			<Divider sx={{ mb: 0.75 }} />
			{documento ? (
				<>
					<Box sx={{ borderRadius: 1, overflow: "hidden", border: "1px solid rgba(0,0,0,0.08)", background: "#fff" }}>
						<iframe
							ref={frame}
							title={`email-${message.id}`}
							sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
							referrerPolicy="no-referrer"
							srcDoc={documento}
							onLoad={aoCarregar}
							style={{ width: "100%", height: alturaVisivel, border: 0, display: "block" }}
						/>
					</Box>
					<Box sx={{ display: "flex", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
						{meta.hasRemoteImages && !remotas && (
							<Button size="small" startIcon={<ImageOutlined />} onClick={() => setRemotas(true)} data-acao="mostrar-imagens">
								Mostrar imagens externas
							</Button>
						)}
						{altura > ALTURA_MAX && (
							<Button
								size="small"
								startIcon={expandido ? <UnfoldLess /> : <UnfoldMore />}
								onClick={() => setExpandido(v => !v)}
							>
								{expandido ? "Recolher" : "Ver e-mail inteiro"}
							</Button>
						)}
					</Box>
				</>
			) : (
				<Typography component="div" sx={{ whiteSpace: "pre-wrap", fontSize: "inherit", wordBreak: "break-word" }}>
					{message.body}
				</Typography>
			)}
		</Box>
	);
};

export default EmailMessageView;
